import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api-context'

/**
 * O atendimento como a paciente o vê (docs/api/portal.md, `GET /portal/appointments/:id`;
 * RN-POR-04, RN-POR-05, RN-POR-15). Os tipos são os do contrato da função `portal`.
 */

export type DocumentStatus =
  | 'generated'
  | 'awaiting_patient'
  | 'awaiting_clinic_signature'
  | 'signed'
  | 'not_signed'
  | 'expired'
  | 'canceled'
  | 'deleted'

export type Level = 'done' | 'waiting' | 'attention' | 'critical'

export interface ClinicRef {
  id: string
  name: string
  timezone: string
}

export interface ClinicContact extends ClinicRef {
  address: string
  phone: string | null
  city: string
  state: string
}

export interface PortalDocument {
  id: string
  appointmentId: string
  name: string
  type: string
  category: string
  position: number
  version: string | null
  status: DocumentStatus
  /** Por extenso, no vocabulário da paciente (RN-POR-05). */
  label: string
  level: Level
  generatedAt: string
  signedAt: string | null
  hash: string | null
  shortHash: string | null
  pageCount: number | null
  hasPdf: boolean
  hasMarkdown: boolean
  /** Falso no importado sem Markdown: só o PDF. */
  canRead: boolean
  isImported: boolean
}

export interface Practitioner {
  name: string
  councilCode: string
  registrationNumber: string | null
}

export interface Appointment {
  id: string
  clinic: ClinicRef
  type: 'procedure' | 'standalone'
  /** `aaaa-mm-dd`: a data do procedimento ou, sem ela, a da geração no fuso da clínica. */
  date: string
  generatedAt: string
  /** Os procedimentos ou, no avulso, os documentos. */
  title: string
  procedures: { name: string; amountCents: number | null }[]
  practitioner: Practitioner | null
  totalAmountCents: number | null
  paymentTerms: string | null
  anamnesisId: string | null
  /** O plano encerrado deste atendimento (RN-POR-23), quando há. */
  treatmentPlanId: string | null
  documents: PortalDocument[]
  /** "2 documentos · assinados". */
  summary: string
  isImported: boolean
}

export interface AppointmentDetail extends Appointment {
  clinic: ClinicContact
}

export const appointmentKeys = {
  detail: (id: string) => ['portal', 'appointment', id] as const,
}

export function useAppointment(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: appointmentKeys.detail(id),
    queryFn: () => api.get<AppointmentDetail>(`/portal/appointments/${id}`),
  })
}
